import styled from "styled-components";
import React, { useEffect, useRef, useState } from "react";

type LottieAnimation = import("lottie-web").AnimationItem;
type RendererSettings = import("lottie-web").SVGRendererConfig;

const LottiePlayerContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export type LottiePlayerContianerProps = React.ComponentPropsWithoutRef<
  typeof LottiePlayerContainer
>;

type Props = LottiePlayerContianerProps & {
  loop?: boolean | number;
  play?: boolean;
  rendererSettings?: RendererSettings;
  animationData: any; // AnimationData = any!
  onEnd?: () => void;
};

export const LottiePlayer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      loop = false,
      play = true,
      animationData,
      rendererSettings,
      onEnd,
      ...rest
    },
    ref
  ) => {
    const [_, setIsLoaded] = useState(false);
    const fallbackRef = useRef<HTMLDivElement>(null);
    const getContainerRef = () => {
      if (ref && "current" in ref) {
        return ref;
      }
      return fallbackRef;
    };
    const lottieRef = useRef<LottieAnimation | null>(null);
    useEffect(() => {
      import("lottie-web").then((module) => {
        const lottie = module.default;
        const container = getContainerRef().current;
        if (container) {
          const anim = lottie.loadAnimation({
            loop,
            autoplay: false,
            container,
            rendererSettings,
            animationData
          });
          anim.addEventListener("complete", () => {
            onEnd?.();
          });
          lottieRef.current = anim;
          setIsLoaded(true);
        }
      });
      return () => {
        if (lottieRef.current) {
          lottieRef.current.destroy();
          lottieRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      const anim = lottieRef.current;
      if (anim) {
        if (play) {
          anim.play();
        } else {
          anim.stop();
        }
      }
    }, [lottieRef.current, play]);

    return <LottiePlayerContainer ref={getContainerRef()} {...rest} />;
  }
);
